import React from 'react';
import { FaSpinner, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { downloadOperationResult } from '../../services/api';
import translations from '../../locales/translations';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import type { StatusColor, OperationState, OperationErrors, OperationResult } from './UserGenerationTypes';
import { useNewGui } from '../../hooks/useNewGui';

interface Props {
  statusMessage: string | null;
  statusColor: StatusColor | null;
  currentOperation: OperationState | null;
  operationErrors: OperationErrors;
  setOperationErrors: React.Dispatch<React.SetStateAction<OperationErrors>>;
  operationResults: OperationResult[];
  lastOperationData: any;
  showResultsModal: boolean;
  setShowResultsModal: (show: boolean) => void;
  updateStatus: (msg: string, color: StatusColor) => void;
}

const OperationProgressPanel: React.FC<Props> = ({
  statusMessage, statusColor, currentOperation, operationErrors,
  setOperationErrors, operationResults, lastOperationData,
  showResultsModal, setShowResultsModal, updateStatus,
}) => {
  const status = useSelector((state: RootState) => state.userGeneration.status);
  const useNew = useNewGui();

  return (
    <>
      <div className={useNew
        ? "mb-4 text-xs text-ink-secondary bg-surface-raised border border-border-subtle rounded px-3 py-2 inline-block"
        : "mb-4 text-sm text-gray-600"
      }>
        <strong className={useNew ? "text-ink-primary" : undefined}>{translations.en.statusLabel}:</strong> {status}
      </div>

      <div className="space-y-2 mt-6">
        {statusMessage && (
          <div className={useNew
            ? `p-3 rounded text-sm ${
                statusColor === 'blue' ? 'bg-info/10 text-info' :
                statusColor === 'red' ? 'bg-danger/10 text-danger' :
                'bg-warning/10 text-warning'
              }`
            : `p-3 rounded-lg ${
                statusColor === 'blue' ? 'bg-blue-100 text-blue-800' :
                statusColor === 'red' ? 'bg-red-100 text-red-800' :
                'bg-orange-100 text-orange-800'
              }`
          }>
            {statusMessage}
          </div>
        )}

        {currentOperation && (
          <div className={useNew ? "bg-surface p-4 rounded-lg border border-border-subtle" : "bg-white p-4 rounded-lg shadow-sm border"}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                {currentOperation.status === 'running' && <FaSpinner className={useNew ? "animate-spin text-info" : "animate-spin text-blue-500"} />}
                {currentOperation.status === 'completed' && <FaCheckCircle className={useNew ? "text-success" : "text-green-500"} />}
                {currentOperation.status === 'failed' && <FaExclamationTriangle className={useNew ? "text-danger" : "text-red-500"} />}
                <span className={useNew ? "font-medium text-ink-primary" : "font-medium"}>Operation Progress</span>
              </div>
              <span className={useNew ? "text-sm text-ink-secondary font-plexMono" : "text-sm text-gray-500"}>
                {currentOperation.operationType.replace('_', ' ').toUpperCase()} #{currentOperation.operationId.slice(-6)}
              </span>
            </div>

            <div className={useNew ? "w-full bg-surface-raised rounded-full h-2 mb-2" : "w-full bg-gray-200 rounded-full h-2 mb-2"}>
              <div
                className={useNew
                  ? `h-2 rounded-full transition-all duration-300 ${
                      currentOperation.status === 'completed' ? 'bg-success' :
                      currentOperation.status === 'failed' ? 'bg-danger' : 'bg-info'
                    }`
                  : `h-2 rounded-full transition-all duration-300 ${
                      currentOperation.status === 'completed' ? 'bg-green-500' :
                      currentOperation.status === 'failed' ? 'bg-red-500' : 'bg-blue-500'
                    }`
                }
                style={{ width: `${currentOperation.progress}%` }}
              />
            </div>

            <div className={useNew ? "flex justify-between text-sm text-ink-secondary" : "flex justify-between text-sm text-gray-600"}>
              <span>{currentOperation.message}</span>
              <span>{currentOperation.progress}%</span>
            </div>

            <div className={useNew ? "mt-2 text-xs text-ink-secondary" : "mt-2 text-xs text-gray-500"}>
              Started: {currentOperation.startTime.toLocaleTimeString()}
              {currentOperation.endTime && (
                <> • Completed: {currentOperation.endTime.toLocaleTimeString()}</>
              )}
            </div>

            {currentOperation.status === 'completed' && (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const result = await downloadOperationResult(currentOperation.operationId);
                      if (result?.blob && result?.filename) {
                        const url = URL.createObjectURL(result.blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = result.filename;
                        a.click();
                        URL.revokeObjectURL(url);
                        updateStatus('Result file downloaded.', 'blue');
                      } else {
                        updateStatus('Result file not available.', 'red');
                      }
                    } catch (e: any) {
                      updateStatus(e?.response?.data?.message || e?.message || 'Download failed.', 'red');
                    }
                  }}
                  className={useNew
                    ? "px-3 py-1.5 text-sm font-medium text-white bg-success hover:opacity-90 rounded transition-opacity"
                    : "px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded"
                  }
                >
                  Download result XLSX
                </button>
              </div>
            )}
          </div>
        )}

        {(operationErrors.skipped > 0 || operationErrors.failed > 0) && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {operationErrors.failed > 0 && (
                  <span className="text-red-600 font-medium">❌ {operationErrors.failed} failed</span>
                )}
                {operationErrors.skipped > 0 && (
                  <span className="text-orange-600 font-medium">⚠️ {operationErrors.skipped} skipped</span>
                )}
              </div>
              {operationErrors.errors.length > 0 && (
                <button
                  onClick={() => setOperationErrors(prev => ({ ...prev, showDetails: !prev.showDetails }))}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  {operationErrors.showDetails ? 'Hide Details' : 'Show Details'}
                </button>
              )}
            </div>
            {operationErrors.showDetails && operationErrors.errors.length > 0 && (
              <div className="mt-3 max-h-40 overflow-y-auto">
                {operationErrors.errors.map((error, index) => (
                  <div key={index} className="mb-2 p-2 bg-white rounded border text-sm text-gray-700">
                    <div className="font-medium text-red-600">
                      {error.type === 'fail' ? 'Failed' : 'Skipped'}: {error.record.username || error.record.email || `Record ${index + 1}`}
                    </div>
                    <div className="text-gray-600 mt-1">{error.error}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {operationErrors.duplicates.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-orange-800 font-medium">
                ⚠️ {operationErrors.duplicates.length} users skipped (already exist)
              </span>
              <button
                onClick={() => setOperationErrors(prev => ({ ...prev, showDuplicateDetails: !prev.showDuplicateDetails }))}
                className="text-orange-600 hover:text-orange-800 text-sm font-medium"
              >
                {operationErrors.showDuplicateDetails ? 'Hide Details' : 'Show Details'}
              </button>
            </div>
            {operationErrors.showDuplicateDetails && (
              <div className="mt-3 max-h-40 overflow-y-auto text-sm text-orange-700">
                {operationErrors.duplicates.map((dup, index) => (
                  <div key={index} className="mb-2 p-2 bg-white rounded border">
                    <div className="font-medium text-orange-600">
                      Skipped: {dup.field === 'email' ? `Email "${dup.value}"` : `User ID "${dup.value}"`}
                    </div>
                    <div className="text-orange-600 mt-1">Existing User ID: {dup.existingUserId}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {lastOperationData?.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="text-red-800 font-medium">Operation Error</div>
            <div className="text-sm text-red-700 mt-1">{lastOperationData.error}</div>
          </div>
        )}

        {operationResults.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <span className="font-medium text-gray-800">Per-User Results</span>
            <div className="max-h-60 overflow-y-auto text-sm mt-2">
              {operationResults.map((result, index) => (
                <div key={index} className="mb-2 p-2 border rounded">
                  <div className="font-medium">
                    {result.userId || result.email || `User ${index + 1}`} — {result.status}
                  </div>
                  <div className="text-gray-600">{result.message}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showResultsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-800">Operation Results</h3>
              <button onClick={() => setShowResultsModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="mb-3">
              <button
                onClick={() => {
                  const csv = lastOperationData?.resultsCsv || '';
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', 'user_generation_results.csv');
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  URL.revokeObjectURL(url);
                }}
                className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Download CSV
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto border rounded">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left p-2">User</th>
                    <th className="text-left p-2">Linux Password</th>
                    <th className="text-left p-2">VNC Password</th>
                    <th className="text-left p-2">VNC Port</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {operationResults.map((result, index) => (
                    <tr key={index} className="border-t">
                      <td className="p-2">{result.userId || result.email || `User ${index + 1}`}</td>
                      <td className="p-2">{result.provisioning?.linuxPassword || ''}</td>
                      <td className="p-2">{result.provisioning?.vncPassword || ''}</td>
                      <td className="p-2">{result.provisioning?.vncPort || ''}</td>
                      <td className="p-2">{result.status}</td>
                      <td className="p-2">{result.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OperationProgressPanel;
